import * as React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface EventNavHeaderProps {
  name: string;
  startDate?: string | null;
  endDate?: string | null;
  status?: string | null;
  /** True when the event has a published schedule version. */
  schedulePublished?: boolean | null;
}

const fmt = (s?: string | null) =>
  s
    ? new Date(`${String(s).slice(0, 10)}T12:00:00`).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      })
    : "";

/**
 * Event context block rendered at the top of the event-scoped sidebar.
 *
 * Two different things are published: the event listing and the schedule. One
 * green word saying "Published" reads as "the schedule is live", which it is
 * not. Both badges are labelled so neither can be mistaken for the other.
 */
export function EventNavHeader({ name, startDate, endDate, status, schedulePublished }: EventNavHeaderProps) {
  const dates = [fmt(startDate), fmt(endDate)].filter(Boolean).join(" – ");
  return (
    <div className="border-b border-sidebar-border px-3 py-3">
      <Link
        to="/admin-dashboard/events"
        className="mb-2 flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-sidebar-foreground"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> All events
      </Link>
      <div className="truncate text-sm font-semibold text-sidebar-foreground">{name}</div>
      <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        {dates && <span>{dates}</span>}
        {status && (
          <Badge variant="secondary" className="h-5 px-1.5 text-[10px]" data-testid="event-status-badge">
            Event {String(status).toLowerCase()}
          </Badge>
        )}
        <Badge
          variant={schedulePublished ? "secondary" : "outline"}
          className="h-5 px-1.5 text-[10px]"
          data-testid="schedule-status-badge"
        >
          Schedule {schedulePublished ? "published" : "draft"}
        </Badge>
      </div>
    </div>
  );
}


export default EventNavHeader;
